import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, Shield, Globe, Palette, Upload,
  ArrowRight, CheckCircle, Star, BarChart2,
  Lock, Clock, Users, ChevronRight, MousePointerClick,
  PencilRuler, Rocket
} from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' },
  }),
};

export default function HomePage() {
  const { t } = useTranslation();

  const stats = [
    { value: '3 saat', label: t('home.stat_deploy') },
    { value: '100%', label: t('home.stat_free') },
    { value: '9', label: t('home.stat_layers') },
    { value: '6', label: t('home.stat_templates') },
  ];

  const features = [
    { icon: Zap, title: t('home.feat1_title'), desc: t('home.feat1_desc') },
    { icon: Globe, title: t('home.feat2_title'), desc: t('home.feat2_desc') },
    { icon: Shield, title: t('home.feat3_title'), desc: t('home.feat3_desc') },
    { icon: Upload, title: t('home.feat4_title'), desc: t('home.feat4_desc') },
    { icon: BarChart2, title: t('home.feat5_title'), desc: t('home.feat5_desc') },
    { icon: Palette, title: t('home.feat6_title'), desc: t('home.feat6_desc') },
  ];

  const securityFeatures = [
    'HTML Sanitizer 7-Lapisan',
    'CSP Auto-Generated',
    'Rate Limiting',
    'IP Allowlist / Blocklist',
    'Cloudflare Turnstile CAPTCHA',
    'Password Protection (bcrypt)',
    'Abuse Detector + Auto-Block',
    'Site Expiry Date',
  ];

  const categories = [
    { emoji: '🎨', name: t('gallery.portfolio'), desc: 'Designer & freelancer' },
    { emoji: '🏢', name: t('gallery.business'), desc: 'SME & korporat' },
    { emoji: '📝', name: t('gallery.blog'), desc: 'Penulis & kreator' },
    { emoji: '🚀', name: t('gallery.landing'), desc: 'Produk & servis' },
    { emoji: '🛍️', name: t('gallery.store'), desc: 'Peniaga online' },
    { emoji: '👤', name: t('gallery.personal'), desc: 'Link-in-bio' },
  ];

  return (
    <div className="hero-bg min-h-screen">

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 bg-white border border-telegram/20 text-telegram rounded-full px-4 py-1.5 text-sm font-medium mb-8 shadow-sm">
            <Zap size={14} className="text-telegram" />
            {t('home.badge')}
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-surface-900 leading-tight mb-6">
            {t('home.hero_line1')}<br />
            <span className="text-gradient">{t('home.hero_line2')}</span>
          </h1>

          <p className="text-xl text-surface-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('home.hero_sub')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/gallery" className="btn-primary btn-xl group">
              {t('home.cta_primary')}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/import" className="btn-outline btn-xl">
              <Upload size={20} />
              {t('home.cta_import')}
            </Link>
          </div>

          {/* URL Custom Preview */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-surface-200 inline-block mb-16">
            <div className="flex items-center gap-2 text-sm">
              <Globe size={16} className="text-telegram" />
              <span className="text-surface-500 font-mono">teleweb.space/s/</span>
              <span className="font-bold text-telegram font-mono">nama-anda</span>
              <span className="badge-green text-xs ml-1">Percuma</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl p-4 shadow-card border border-surface-200">
                <div className="text-3xl font-extrabold text-telegram mb-1">{stat.value}</div>
                <div className="text-sm text-surface-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ─── INFOGRAFIK 3 KLIK ─────────────────────────────────────── */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="section-title text-3xl sm:text-4xl mb-3">{t('home.how_title')}</h2>
            <p className="section-subtitle text-lg">{t('home.how_sub')}</p>
          </div>

          {/* 3 Steps Visual — difahami bawah 3 saat */}
          <div className="grid md:grid-cols-3 gap-6">

            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl p-8 h-full border-2 border-purple-200">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 bg-purple-500 rounded-2xl flex items-center justify-center text-white">
                    <MousePointerClick size={24} />
                  </div>
                  <span className="text-5xl font-extrabold text-purple-200">1</span>
                </div>
                <div className="text-2xl font-extrabold text-surface-900 mb-2">Klik</div>
                <h3 className="text-lg font-bold text-surface-900 mb-2">{t('home.step1_title')}</h3>
                <p className="text-surface-600 text-sm leading-relaxed">{t('home.step1_desc')}</p>
                {/* Visual mock */}
                <div className="mt-4 bg-white rounded-xl p-3 flex gap-2">
                  {['🎨', '🏢', '📝'].map((e, i) => (
                    <div key={i} className="flex-1 h-10 bg-surface-100 rounded-lg flex items-center justify-center text-lg animate-pulse-soft" style={{ animationDelay: `${i * 0.3}s` }}>
                      {e}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 h-full border-2 border-blue-200">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center text-white">
                    <PencilRuler size={24} />
                  </div>
                  <span className="text-5xl font-extrabold text-blue-200">2</span>
                </div>
                <div className="text-2xl font-extrabold text-surface-900 mb-2">Klik</div>
                <h3 className="text-lg font-bold text-surface-900 mb-2">{t('home.step2_title')}</h3>
                <p className="text-surface-600 text-sm leading-relaxed">{t('home.step2_desc')}</p>
                {/* Visual mock — editing */}
                <div className="mt-4 bg-white rounded-xl p-3 space-y-2">
                  <div className="h-3 bg-blue-200 rounded w-3/4 animate-pulse-soft" />
                  <div className="h-3 bg-blue-100 rounded w-full animate-pulse-soft" style={{ animationDelay: '0.2s' }} />
                  <div className="h-3 bg-blue-100 rounded w-2/3 animate-pulse-soft" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
              {/* Connector arrow (desktop) */}
              <div className="hidden md:block absolute top-1/2 -left-4 transform -translate-y-1/2 z-10">
                <ChevronRight size={28} className="text-surface-300" />
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8 h-full border-2 border-green-200">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 bg-green-500 rounded-2xl flex items-center justify-center text-white">
                    <Rocket size={24} />
                  </div>
                  <span className="text-5xl font-extrabold text-green-200">3</span>
                </div>
                <div className="text-2xl font-extrabold text-surface-900 mb-2">Klik</div>
                <h3 className="text-lg font-bold text-surface-900 mb-2">{t('home.step3_title')}</h3>
                <p className="text-surface-600 text-sm leading-relaxed">{t('home.step3_desc')}</p>
                {/* Visual mock — live */}
                <div className="mt-4 bg-white rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse-soft" />
                    <span className="text-xs font-mono text-surface-600">teleweb.space/s/nama-anda</span>
                  </div>
                  <div className="text-xs text-green-600 font-bold mt-1">✓ LIVE</div>
                </div>
              </div>
              {/* Connector arrow (desktop) */}
              <div className="hidden md:block absolute top-1/2 -left-4 transform -translate-y-1/2 z-10">
                <ChevronRight size={28} className="text-surface-300" />
              </div>
            </motion.div>
          </div>

          {/* Summary bar */}
          <div className="mt-10 bg-gradient-to-r from-telegram to-blue-600 rounded-2xl p-6 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-white">
              <Zap size={24} />
              <span className="text-xl font-extrabold">
                Pilih → Edit → Publish ={' '}
                <span className="underline decoration-4 decoration-yellow-300">URL anda LIVE dalam 3 saat</span>
              </span>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/gallery" className="btn-primary btn-lg group">
              {t('home.try_now')}
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─────────────────────────────────────────────── */}
      <section className="py-20 bg-surface-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="section-title text-3xl sm:text-4xl mb-3">{t('home.features_title')}</h2>
            <p className="section-subtitle text-lg">{t('home.features_sub')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={f.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="card card-hover p-6">
                <div className="w-12 h-12 bg-telegram/10 rounded-xl flex items-center justify-center mb-4">
                  <f.icon size={22} className="text-telegram" />
                </div>
                <h3 className="font-bold text-surface-900 mb-2">{f.title}</h3>
                <p className="text-surface-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECURITY SECTION ─────────────────────────────────────── */}
      <section className="py-20 bg-surface-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-telegram text-sm font-medium mb-4">
                <Shield size={16} />
                {t('home.sec_badge')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6 leading-tight">{t('home.sec_title')}</h2>
              <p className="text-surface-400 text-lg leading-relaxed mb-8">{t('home.sec_sub')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {securityFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-surface-300 text-sm">
                    <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-surface-800 rounded-3xl p-8 border border-surface-700">
              <div className="text-center mb-6">
                <div className="relative inline-flex items-center justify-center">
                  <svg width="140" height="140" className="score-ring">
                    <circle cx="70" cy="70" r="56" stroke="#1e293b" strokeWidth="10" fill="none" />
                    <circle cx="70" cy="70" r="56" stroke="#229ED9" strokeWidth="10" fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - 0.85)}`}
                      strokeLinecap="round" />
                  </svg>
                  <div className="absolute text-center">
                    <div className="text-4xl font-extrabold text-white">85</div>
                    <div className="text-surface-400 text-sm">/ 100</div>
                  </div>
                </div>
                <div className="text-white font-bold text-lg mt-2">{t('home.sec_score')}</div>
                <div className="badge-green mt-1">{t('home.sec_good')}</div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'HTTPS (Auto)', value: true },
                  { label: 'CSP Header', value: true },
                  { label: 'Rate Limiting', value: true },
                  { label: 'Sanitizer HTML', value: true },
                  { label: 'Password', value: false },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-surface-700 last:border-0">
                    <span className="text-surface-300 text-sm">{item.label}</span>
                    <span className={item.value ? 'badge-green' : 'badge-gray'}>
                      {item.value ? t('home.sec_active') : t('home.sec_inactive')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TEMPLATES PREVIEW ────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="section-title text-3xl mb-2">{t('home.templates_title')}</h2>
              <p className="section-subtitle">{t('home.templates_sub')}</p>
            </div>
            <Link to="/gallery" className="btn-outline btn-md hidden sm:flex">
              {t('home.templates_view')}
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link key={cat.name} to="/gallery"
                className="bg-surface-50 border border-surface-200 rounded-2xl p-4 text-center hover:shadow-card hover:border-telegram/30 transition-all hover:-translate-y-1 cursor-pointer group">
                <div className="text-3xl mb-2">{cat.emoji}</div>
                <div className="font-bold text-surface-900 text-sm">{cat.name}</div>
                <div className="text-surface-500 text-xs mt-0.5">{cat.desc}</div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8 sm:hidden">
            <Link to="/gallery" className="btn-primary btn-md">{t('home.templates_view')}</Link>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ────────────────────────────────────────────── */}
      <section className="py-24 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="text-6xl mb-6">⚡</div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-surface-900 mb-6 leading-tight">{t('home.final_title')}</h2>
            <p className="text-xl text-surface-500 mb-10">{t('home.final_sub')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/gallery" className="btn-primary btn-xl group">
                <Star size={20} />
                {t('home.cta_primary')}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/import" className="btn-outline btn-xl">
                <Upload size={20} />
                {t('home.cta_import')}
              </Link>
            </div>
            <p className="text-surface-400 text-sm mt-6">{t('home.final_note')}</p>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────────── */}
      <footer className="bg-surface-900 text-surface-400 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 telegram-gradient rounded-md flex items-center justify-center">
                <span className="text-white text-xs font-bold">T</span>
              </div>
              <span className="text-white font-bold">TeleWeb</span>
            </div>
            <p className="text-sm">© 2026 TeleWeb. {t('home.footer_tag')}</p>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1"><Lock size={12} /> HTTPS</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {t('home.footer_uptime')}</span>
              <span className="flex items-center gap-1"><Users size={12} /> {t('home.footer_users')}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
